import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatPrice } from "../../../utils/priceHelpers";
import { formatDateToDisplay } from "../../../utils/dateTimeHelpers";
import { applyPromo } from "services/basket.services";
import { MessageBox } from "reusableComponents";
import styles from "./styles.module.scss";
import { getStreetName } from "views/SpacePage/helpers";

const BookingOverview = ({ 
  space, 
  formData, 
  servicesData, 
  basketData,
  promoCode, 
  setPromoCode, 
  isCoworking,
  dispositionTypes,
  submitting,
  handleReserve,
  basketId,
  token,
  providerId,
  onPromoApplied,
  hasDiscount = false,
  displayDiscount = 0,
  displayTotal = 0,
  displayOriginalTotal = 0,
  promoPercentage = 0,
  isTrainee = false,
  isSubmitDisabled = false,
  isServicesSaving = false,
  cguAccepted = false,
  setCguAccepted = () => {},
  cguError = false,
  setCguError = () => {},
  basketRefreshKey = 0,
  // ✅ New props for queue state
  queueLength = 0,
  isProcessingQueue = false,
  t
}) => {
  const { i18n } = useTranslation();
  const currentLocale = i18n?.language || 'en';
  
  const [localPromoCode, setLocalPromoCode] = useState(promoCode || "");
  const [promoErrorMessage, setPromoErrorMessage] = useState(null);
  const [promoSuccessMessage, setPromoSuccessMessage] = useState(null);
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(displayDiscount);

  useEffect(() => {
    console.log('[BookingOverview] Basket data updated:', basketData);
  }, [basketData]);

  useEffect(() => {
    console.log('[BookingOverview] 🔄 isServicesSaving prop received:', isServicesSaving);
  }, [isServicesSaving]);

  useEffect(() => {
    console.log('[BookingOverview] Basket refresh key:', basketRefreshKey);
  }, [basketRefreshKey]);

  useEffect(() => {
    setAppliedDiscount(displayDiscount);
  }, [displayDiscount]);

  useEffect(() => {
    if (promoCode && !localPromoCode) {
      setLocalPromoCode(promoCode);
    }
  }, [promoCode]);

  // Real-time local services total calculation
  const localServicesTotal = useMemo(() => {
    let total = 0;
    
    formData.day_services?.forEach(dayService => {
      dayService.services?.forEach(selected => {
        const dayServices = servicesData?.[dayService.day_id];
        const serviceData = dayServices?.find(
          s => s.service?.place_service?.id === selected.service_id
        );
        
        if (serviceData) {
          if (serviceData.values && serviceData.values[selected.quantity] !== undefined) {
            total += serviceData.values[selected.quantity];
          } else if (serviceData.service?.price) {
            total += serviceData.service.price * selected.quantity;
          } else if (serviceData.price) {
            total += serviceData.price * selected.quantity;
          }
        }
      });
    });
    
    return total;
  }, [formData.day_services, servicesData]);

  const servicesDisplay = useMemo(() => {
    if (!formData.day_services || formData.day_services.length === 0) {
      return [];
    }
    
    const services = [];
    
    formData.day_services.forEach(day => {
      if (!day.services || day.services.length === 0) return;
      
      day.services.forEach(service => {
        const dayServices = servicesData?.[day.day_id];
        const serviceData = dayServices?.find(
          s => s.service?.place_service?.id === service.service_id
        );
        
        let price = 0;
        if (serviceData?.values && serviceData.values[service.quantity] !== undefined) {
          price = serviceData.values[service.quantity];
        } else if (serviceData?.service?.price) {
          price = serviceData.service.price * service.quantity;
        } else if (serviceData?.price) {
          price = serviceData.price * service.quantity;
        }
        
        const serviceName = serviceData?.service?.place_service?.name || 
                           serviceData?.service?.name || 
                           serviceData?.name ||
                           `Service ${service.service_id}`;
        
        // ✅ Check if service is paid onsite
        const isPaidOnsite = serviceData?.service?.location_type === "onsite" || 
                            serviceData?.service?.type === "onsite" ||
                            serviceData?.service?.place_service?.type === "onsite";
        
        services.push({
          id: service.service_id,
          name: serviceName,
          quantity: service.quantity,
          price: price,
          dayId: day.day_id,
          isFree: price === 0,
          isPaidOnsite: isPaidOnsite // ✅ Add this property
        });
      });
    });
    
    return services;
  }, [formData.day_services, servicesData]);

  const handleApplyPromoClick = async () => {
    setPromoErrorMessage(null);
    setPromoSuccessMessage(null);
    
    if (!localPromoCode.trim()) {
      setPromoErrorMessage(t("booking.fullBookingPage.promo.required") || "Please enter a promo code.");
      return;
    }
    
    if (!basketId) {
      setPromoErrorMessage(t("booking.fullBookingPage.promo.basketRequired") || "Unable to apply promo code at this time.");
      return;
    }
    
    if (!token) {
      setPromoErrorMessage(t("booking.fullBookingPage.promo.loginRequired") || "Please login to apply promo code.");
      return;
    }
    
    setIsApplyingPromo(true);
    
    try {
      const response = await applyPromo(token, basketId, localPromoCode.trim(), providerId);
      
      if (response.success) {
        setPromoSuccessMessage(
          response.message || t("booking.fullBookingPage.promo.success") || "Promo code applied successfully!"
        );
        
        const discountAmount = response.discount || 0;
        setAppliedDiscount(discountAmount);
        setPromoCode(localPromoCode);
        setLocalPromoCode("");
        
        if (onPromoApplied) {
          onPromoApplied(discountAmount);
        }
        
        setTimeout(() => {
          setPromoSuccessMessage(null);
        }, 5000);
      } else {
        const errorMessage = response.errors?.[0]?.message || 
                            response.message || 
                            t("booking.fullBookingPage.promo.invalid") || 
                            "Promo code is invalid or expired.";
        setPromoErrorMessage(errorMessage);
      }
    } catch (error) {
      console.error("Error applying promo code:", error);
      
      let errorMsg = t("booking.fullBookingPage.promo.error") || "Failed to apply promo code. Please try again.";
      
      if (error.response?.status === 401) {
        errorMsg = t("booking.fullBookingPage.promo.loginRequired") || "Please login to apply promo code.";
      } else if (error.response?.status === 404) {
        errorMsg = t("booking.fullBookingPage.promo.invalid") || "Promo code is invalid or expired.";
      } else if (error.response?.data?.errors) {
        errorMsg = error.response.data.errors.map(e => e.message).join(', ');
      }
      
      setPromoErrorMessage(errorMsg);
    } finally {
      setIsApplyingPromo(false);
    }
  };

  const handleRemovePromo = async () => {
    setAppliedDiscount(0);
    setPromoCode("");
    setPromoErrorMessage(null);
    setPromoSuccessMessage(null);
    
    if (onPromoApplied) {
      onPromoApplied(0);
    }
  };

  const handleReserveClick = () => {
    // ✅ FIX: Double-check queue state before proceeding
    if (queueLength > 0 || isProcessingQueue) {
      console.warn('[BookingOverview] Services still saving, preventing submission');
      return;
    }
    
    if (typeof handleReserve === 'function') {
      handleReserve();
    }
  };

  const finalDiscount = appliedDiscount > 0 ? appliedDiscount : displayDiscount;
  const hasBookingHours = formData.booking_hours && formData.booking_hours.length > 0;
  const hasServicesList = servicesDisplay.length > 0;
  
  // ✅ FIX: Include queue state in button disabled check
  const isButtonDisabled = isSubmitDisabled || 
                          submitting || 
                          !hasBookingHours || 
                          isApplyingPromo ||
                          queueLength > 0 ||
                          isProcessingQueue;
  
  const isButtonLoading = isServicesSaving || submitting || queueLength > 0 || isProcessingQueue;

  const getButtonText = () => {
    if (isServicesSaving || queueLength > 0 || isProcessingQueue) {
      return t("booking.fullBookingPage.overview.saving", "Saving...");
    }
    if (submitting) {
      return t("booking.fullBookingPage.overview.processing", "Processing...");
    }
    return isTrainee 
      ? t("booking.fullBookingPage.overview.reserve", "Reserve")
      : t("booking.fullBookingPage.overview.payAndReserve", "Pay & Reserve");
  };

  return (
    <div className={styles.overviewCard}>
      {space?.images?.[0] && (
        <img 
          src={space.images[0].image_url} 
          alt={space.name || "Space"} 
          className={styles.spaceImage} 
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      )}
      
      <div className={styles.dispositionCard}>
        <div className={styles.cardName}>{space?.name || ""}</div>
<div className={styles.cardAddress}>
  {[
    getStreetName(space?.address?.address),
    space?.address?.postal_code,
    space?.address?.city,
    space?.address?.country,
  ]
    .filter(Boolean)
    .join(", ")}
</div>     </div>
      
      <div className={styles.sectionTitle}>
        {t("booking.fullBookingPage.overview.bookingDetails")}
      </div>
      
      <div className={styles.dispositionCard}>
        {hasBookingHours ? (
          formData.booking_hours.map((slot, idx) => (
            <div key={idx} className={styles.cardRow}>
              <span>{formatDateToDisplay(slot.date, currentLocale)}</span>
              <span>{slot.from} - {slot.to}</span>
            </div>
          ))
        ) : (
          <div className={styles.cardRow}>
            <span className={styles.noSelection}>
              {t("booking.fullBookingPage.overview.noSlotSelected") || "No time slot selected"}
            </span>
          </div>
        )}
        <div className={styles.cardRow}>
          <span>{t("booking.fullBookingPage.overview.people")}</span>
          <span>{isCoworking ? formData.coworking_stations : formData.capacity}</span>
        </div>
        {formData.disposition_type && (
          <div className={styles.cardRow}>
            <span>{t("booking.fullBookingPage.overview.layout")}</span>
            <span>{dispositionTypes?.find(d => d.key === formData.disposition_type)?.name || formData.disposition_type}</span>
          </div>
        )}
      </div>
      
      {hasServicesList && (
        <div className={styles.dispositionCard}>
          <div className={styles.servicesTitle}>
            {t("booking.fullBookingPage.overview.services") || "Services"}
          </div>
          {servicesDisplay.map((service, idx) => (
            <div key={`${service.dayId}-${service.id}-${idx}`} className={styles.cardRow}>
              <span>
                {service.name}
                {service.quantity > 1 && ` x${service.quantity}`}
                {/* ✅ Show Paid Onsite badge in overview */}
                {service.isPaidOnsite && (
                  <span 
                    className={styles.paidOnsiteBadgeSmall}
                    title={t("booking.fullBookingPage.services.paidOnsiteTooltip", "This service is paid directly at the venue")}
                  >
                    💳 {t("booking.fullBookingPage.services.paidOnsite", "Pay on-site")}
                  </span>
                )}
              </span>
              {!isTrainee && (
                <span className={service.isFree ? styles.freeService : ""}>
                  {service.isFree 
                    ? (t("booking.fullBookingPage.overview.free") || "Free")
                    : formatPrice(service.price, space?.currency || "EUR")
                  }
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      
      {!isTrainee && (
        <div className={styles.promoCard}>
          <div className={styles.promoContainer}>
            <input 
              type="text" 
              placeholder={t("booking.fullBookingPage.promo.placeholder") || "Enter promo code"} 
              value={localPromoCode} 
              onChange={(e) => {
                setLocalPromoCode(e.target.value);
                setPromoErrorMessage(null);
                setPromoSuccessMessage(null);
              }} 
              className={styles.promoInput} 
              disabled={isApplyingPromo || submitting}
            />
            <button 
              onClick={handleApplyPromoClick} 
              className={styles.applyButton}
              disabled={!localPromoCode.trim() || isApplyingPromo || submitting}
            >
              {isApplyingPromo ? 
                (t("booking.fullBookingPage.promo.applying") || "Applying...") : 
                (t("booking.fullBookingPage.promo.apply") || "Apply")}
            </button>
          </div>
          
          {finalDiscount > 0 && promoCode && (
            <div className={styles.appliedPromoCard}>
              <span className={styles.appliedPromoCode}>
                {t("booking.fullBookingPage.promo.applied") || "Applied:"} {promoCode}
              </span>
              <button 
                onClick={handleRemovePromo} 
                className={styles.removePromoButton}
                disabled={submitting}
              >
                ✕
              </button>
            </div>
          )}
          
          {promoSuccessMessage && (
            <div className={styles.promoSuccessMessage}>
              {promoSuccessMessage}
            </div>
          )}
          
          {promoErrorMessage && (
            <div className={styles.promoErrorMessage}>
              {promoErrorMessage}
            </div>
          )}
        </div>
      )}
      
      {/* PRICE SECTION */}
      {!isTrainee && (
        <div className={styles.dispositionCard}>         
          
          {hasDiscount && finalDiscount > 0 && (
            <div className={styles.cardRow}>
              <span>
                {t("booking.fullBookingPage.overview.discount") || "Discount"}
                {promoPercentage > 0 && ` (${Math.round(promoPercentage)}%)`}
              </span>
              <span className={styles.discountAmount}>
                -{formatPrice(finalDiscount, space?.currency || "EUR")}
              </span>
            </div>
          )}
          
          <div className={`${styles.cardRow} ${styles.totalRow}`}>
            <span>{t("booking.fullBookingPage.overview.total") || "Total"}</span>
            <span className={`${styles.totalAmount} ${hasDiscount ? styles.discountedTotal : ""}`}>
              {formatPrice(displayTotal, space?.currency || "EUR")}
            </span>
          </div>
          
          {hasDiscount && displayOriginalTotal > 0 && displayOriginalTotal !== displayTotal && (
            <div className={`${styles.cardRow} ${styles.originalTotalRow}`}>
              <span>{t("booking.fullBookingPage.overview.originalTotal") || "Original total"}</span>
              <span className={styles.crossedOut}>
                {formatPrice(displayOriginalTotal, space?.currency || "EUR")}
              </span>
            </div>
          )}
        </div>
      )}
      
      <div className={styles.termsSection}>
        <label className={styles.termsCheckbox}>
          <input 
            type="checkbox" 
            checked={cguAccepted} 
            onChange={(e) => {
              setCguAccepted(e.target.checked);
              if (e.target.checked) {
                setCguError(false);
              }
            }} 
          />
          <span>
            {t("booking.fullBookingPage.terms.accept")}{" "}
            <a href="/static/cgu" target="_blank" rel="noopener noreferrer">
              {t("booking.fullBookingPage.terms.link")}
            </a>
          </span>
        </label>
        {cguError && (
          <MessageBox 
            type="error" 
            message={t("booking.fullBookingPage.errors.termsRequired")} 
          />
        )}
      </div>
      
      <div className={styles.cancellationText}>
        {t("booking.fullBookingPage.overview.cancellationText") || "Free cancellation up to 24 hours before booking"}
      </div>
      
      {/* Minimal button - just disabled with text change */}
      <button 
        className={`${styles.reserveButton} ${isButtonLoading ? styles.loading : ""}`}
        onClick={handleReserveClick}
        disabled={isButtonDisabled}
      >
        {isButtonLoading && <span className={styles.spinnerSmall}></span>}
        {getButtonText()}
      </button>
    </div>
  );
};

export default BookingOverview;